import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Avatar,
  Box,
  Menu,
  MenuItem,
  IconButton,
  Chip,
  useTheme,
  useMediaQuery,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import LogoutIcon from '@mui/icons-material/Logout';
import LoginIcon from '@mui/icons-material/Login';
import PersonIcon from '@mui/icons-material/Person';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import QuestionAnswerIcon from '@mui/icons-material/QuestionAnswer';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import AssignmentIcon from '@mui/icons-material/Assignment';
import AssessmentIcon from '@mui/icons-material/Assessment';
import HomeIcon from '@mui/icons-material/Home';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import { 
  selectCurrentUser, 
  selectIsAdmin 
} from '../store/slices/authSlice';
import { useAuth } from '../contexts/AuthContext';
import Logo from '../assets/images/Logo.png';
import { BarChart } from '@mui/icons-material';
import RecordVoiceOverIcon from '@mui/icons-material/RecordVoiceOver';
import CreateIcon from '@mui/icons-material/Create';
import ScienceIcon from '@mui/icons-material/Science';
import BookmarksIcon from '@mui/icons-material/Bookmarks';
import QuizIcon from '@mui/icons-material/Quiz';

const navigationItems = [
  { text: 'Home', icon: <HomeIcon />, path: '/' },
  { text: 'Public Mock Tests', icon: <QuizIcon />, path: '/mock-tests' },
  { text: 'TOEIC Mock Tests', icon: <AssignmentIcon />, path: '/my-examinations' },
  { text: 'Recording and Homework', icon: <MenuBookIcon />, path: '/my-exercises' },
  { text: 'Trial Laboratory', icon: <ScienceIcon />, path: '/trial-laboratory' },
  { text: 'Memories', icon: <BookmarksIcon />, path: '/memories' },
  // { text: 'Speaking Practice', icon: <RecordVoiceOverIcon />, path: '/speaking-practice' },
  // { text: 'Writing Practice', icon: <CreateIcon />, path: '/writing-practice' },
];

const Navbar = ({ handleDrawerToggle }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const location = useLocation();
  const { logout } = useAuth();
  const currentUser = useSelector(selectCurrentUser);
  const isAdmin = useSelector(selectIsAdmin);
  const [anchorEl, setAnchorEl] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    handleClose();
    logout();
  };

  const handleAdminDashboard = () => {
    navigate('/admin');
    handleClose();
  };

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const handleNavigate = (path) => {
    navigate(path);
    setMobileMenuOpen(false);
  };



  return (
    <AppBar 
      position="static"
      sx={{
        background: 'white',
        boxShadow: '0 4px 20px rgba(33, 150, 243, 0.2)',
        backdropFilter: 'blur(10px)'
      }}
    >
      {/* Top Row - Logo and Profile */}
      <Toolbar sx={{ py: 1 }}>
        {isMobile && currentUser && (
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={toggleMobileMenu}
            sx={{ mr: 2, display: { md: 'none' }, color: '#2196F3' }}
          >
            <MenuIcon />
          </IconButton>
        )}
        <Box 
          component="img"
          src={Logo}
          alt="STUDY TOEIC WITH MR. S"
          sx={{ 
            height: 40, 
            width: 40,
            mr: 2,
            cursor: 'pointer',
            objectFit: 'cover',
            objectPosition: 'top',
            borderRadius: 2,
            border: '1px solid rgba(33, 150, 243, 0.2)',
          }}
          onClick={() => navigate('/home')}
        />
        <Typography 
          variant="h5" 
          component="div" 
          sx={{ 
            fontWeight: 700,
            background: 'linear-gradient(45deg, #2196F3 0%, #9C27B0 50%, #00BCD4 100%)',
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            textShadow: '0 2px 4px rgba(0,0,0,0.1)',
            cursor: 'pointer',
            display: { xs: 'none', sm: 'block' },
          }}
          onClick={() => navigate('/home')}
        >
          STUDY TOEIC WITH MR. S
        </Typography>

        <Box sx={{ flexGrow: 1 }} />

        {!currentUser && (
          <Button
            variant="contained"
            startIcon={<LoginIcon />}
            onClick={() => navigate('/login')}
            sx={{
              background: 'linear-gradient(45deg, #2196F3 0%, #1976D2 100%)',
              color: 'white',
              fontWeight: 600,
              textTransform: 'none',
              borderRadius: 2,
              px: 3,
              py: 1,
              fontSize: '0.9rem',
              boxShadow: '0 4px 12px rgba(33, 150, 243, 0.3)',
              '&:hover': {
                background: 'linear-gradient(45deg, #1976D2 0%, #1565C0 100%)',
                boxShadow: '0 6px 16px rgba(33, 150, 243, 0.4)',
              },
            }}
          >
            Login
          </Button>
        )}

        {currentUser && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {/* Welcome Text */}
            <Box sx={{ display: { xs: 'none', md: 'block' }, textAlign: 'right', mr: 1 }}>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 600,
                  color: '#2c3e50',
                  lineHeight: 1.2,
                }}
              >
                Welcome back, {currentUser.lastName || currentUser.name?.split(' ')[currentUser.name?.split(' ').length - 1] || 'Student'}!
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  color: '#546e7a',
                  fontSize: '0.7rem',
                }}
              >
                Keep learning, stay motivated
              </Typography>
            </Box>

            {/* Avatar with Badges Below */}
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
              {/* User Menu */}
              <IconButton
                size="large"
                aria-label="account of current user"
                aria-controls="menu-appbar"
                aria-haspopup="true"
                onClick={handleMenu}
                sx={{
                  color: '#667eea',
                  background: 'rgba(102, 126, 234, 0.1)',
                  border: '1px solid rgba(33, 150, 243, 0.2)',
                  transition: 'all 0.3s ease',
                  p: 0.5,
                  '&:hover': {
                    background: 'rgba(33, 150, 243, 0.15)',
                    transform: 'scale(1.05)',
                    boxShadow: '0 4px 12px rgba(33, 150, 243, 0.2)'
                  }
                }}
              >
                {currentUser.picture ? (
                  <Avatar
                    src={currentUser.picture}
                    alt={currentUser.name}
                    sx={{ 
                      width: 40, 
                      height: 40,
                      border: '2px solid rgba(33, 150, 243, 0.3)',
                      boxShadow: '0 4px 12px rgba(33, 150, 243, 0.2)'
                    }}
                  />
                ) : (
                  <AccountCircleIcon sx={{ fontSize: 40 }} />
                )}
              </IconButton>

              {/* Badges Row */}
              
            </Box>

            <Menu
              id="menu-appbar"
              anchorEl={anchorEl}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}
              keepMounted
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              open={Boolean(anchorEl)}
              onClose={handleClose}
              sx={{
                '& .MuiPaper-root': {
                  background: 'rgba(255,255,255,0.95)',
                  backdropFilter: 'blur(20px)',
                  borderRadius: 3,
                  border: '1px solid rgba(33, 150, 243, 0.2)',
                  boxShadow: '0 8px 32px rgba(33, 150, 243, 0.15)',
                  mt: 1,
                  minWidth: 200
                }
              }}
            >
              <MenuItem 
                onClick={handleClose} 
                disabled
                sx={{
                  borderBottom: '1px solid rgba(0,0,0,0.1)',
                  mb: 1,
                  '&.Mui-disabled': {
                    opacity: 1
                  }
                }}
              >
                <Box sx={{ width: '100%' }}>
                  <Typography 
                    variant="subtitle2" 
                    sx={{ 
                      fontWeight: 600,
                      background: 'linear-gradient(45deg, #2196F3 0%, #1565C0 100%)',
                      backgroundClip: 'text',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent'
                    }}
                  >
                    {currentUser.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    {currentUser.email}
                  </Typography>
                  {currentUser.role === 'student' && currentUser.class && (
                    <Chip
                      label={currentUser.class.classCode || currentUser.class.className}
                      size="small"
                      sx={{
                        mt: 0.5,
                        height: 20,
                        fontSize: '0.7rem',
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        color: 'white',
                        fontWeight: 500,
                      }}
                    />
                  )}
                </Box>
              </MenuItem>
              
              <MenuItem 
                onClick={() => {
                  handleClose();
                  navigate('/profile');
                }}
                sx={{
                  borderRadius: 2,
                  mx: 1,
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    background: '#f5f5f5',
                    transform: 'translateX(4px)'
                  }
                }}
              >
                <PersonIcon sx={{ mr: 1, color: '#0288D1' }} />
                My Profile
              </MenuItem>
              
              <MenuItem 
                onClick={() => {
                  handleClose();
                  navigate('/statistics');
                }}
                sx={{
                  borderRadius: 2,
                  mx: 1,
                  mb: 0.5,
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    background: '#f5f5f5',
                    transform: 'translateX(4px)'
                  }
                }}
              >
                <BarChart sx={{ mr: 1, color: '#fc41a2' }} />
                My Progress
              </MenuItem>
              {isAdmin && (
                <>
                  <MenuItem 
                    onClick={handleAdminDashboard}
                    sx={{
                      borderRadius: 2,
                      mx: 1,
                      mb: 0.5,
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        background: 'linear-gradient(45deg, rgba(33, 150, 243, 0.1) 0%, rgba(21, 101, 192, 0.1) 100%)',
                        transform: 'translateX(4px)'
                      }
                    }}
                  >
                    <AdminPanelSettingsIcon sx={{ mr: 1, color: '#9C27B0' }} />
                    Admin Dashboard
                  </MenuItem>
                  <MenuItem 
                    onClick={() => { navigate('/admin/exam-assignments'); handleClose(); }}
                    sx={{
                      py: 1.5,
                      px: 2,
                      '&:hover': {
                        bgcolor: 'rgba(76, 175, 80, 0.1)',
                      }
                    }}
                  >
                    <AssignmentIcon sx={{ mr: 1, color: '#4CAF50' }} />
                    Assignments Management
                  </MenuItem>
                  <MenuItem 
                    onClick={() => { navigate('/admin/reports'); handleClose(); }}
                    sx={{
                      py: 1.5,
                      px: 2,
                      '&:hover': {
                        bgcolor: 'rgba(255, 152, 0, 0.1)',
                      }
                    }}
                  >
                    <AssessmentIcon sx={{ mr: 1, color: '#FF9800' }} />
                    Class Reports
                  </MenuItem>
                  {/* <MenuItem 
                    onClick={() => { navigate('/admin/exam-attempts'); handleClose(); }}
                    sx={{
                      borderRadius: 2,
                      mx: 1,
                      mb: 0.5,
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        background: 'linear-gradient(45deg, rgba(255, 152, 0, 0.1) 0%, rgba(245, 124, 0, 0.1) 100%)',
                        transform: 'translateX(4px)'
                      }
                    }}
                  >
                    <AssessmentIcon sx={{ mr: 1, color: '#0288D1' }} />
                    Exam Attempts
                  </MenuItem> */}
                  {/* <MenuItem 
                    onClick={() => { navigate('/admin/questions'); handleClose(); }}
                    sx={{
                      borderRadius: 2,
                      mx: 1,
                      mb: 0.5,
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        background: 'linear-gradient(45deg, rgba(33, 150, 243, 0.1) 0%, rgba(21, 101, 192, 0.1) 100%)',
                        transform: 'translateX(4px)'
                      }
                    }}
                  >
                    <QuestionAnswerIcon sx={{ mr: 1, color: '#00BCD4' }} />
                    Question Management
                  </MenuItem>
                  <MenuItem 
                    onClick={() => { navigate('/admin/content'); handleClose(); }}
                    sx={{
                      borderRadius: 2,
                      mx: 1,
                      mb: 0.5,
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        background: 'linear-gradient(45deg, rgba(33, 150, 243, 0.1) 0%, rgba(21, 101, 192, 0.1) 100%)',
                        transform: 'translateX(4px)'
                      }
                    }}
                  >
                    <ContentCopyIcon sx={{ mr: 1, color: '#4CAF50' }} />
                    Content Management
                  </MenuItem> */}
                </>
              )}
              
              <MenuItem 
                onClick={handleLogout}
                sx={{
                  borderRadius: 2,
                  mx: 1,
                  mt: 1,
                  borderTop: '1px solid rgba(0,0,0,0.1)',
                  pt: 1.5,
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    background: 'linear-gradient(45deg, rgba(245, 87, 108, 0.1) 0%, rgba(240, 147, 251, 0.1) 100%)',
                    transform: 'translateX(4px)'
                  }
                }}
              >
                <LogoutIcon sx={{ mr: 1, color: '#E91E63' }} />
                Sign Out
              </MenuItem>
            </Menu>
          </Box>
        )}
      </Toolbar>

      {/* Bottom Row - Navigation Menu (Desktop only) - Tab Style */}
      {currentUser && (
        <Box 
          sx={{ 
            display: { xs: 'none', md: 'flex' },
            px: 3,
            py: 0,
          }}
        >
          {navigationItems.map((item) => {
            const isActive = location.pathname === item.path;
            return (
              <Button
                key={item.path}
                onClick={() => navigate(item.path)}
                startIcon={item.icon}
                sx={{
                  color: isActive ? '#2196F3' : '#555',
                  px: 2.5,
                  py: 1.5,
                  textTransform: 'none',
                  fontWeight: isActive ? 600 : 500,
                  fontSize: '0.95rem',
                  borderRadius: 0,
                  borderTop: isActive ? '3px solid #2196F3' : '3px solid transparent',
                  background: isActive ? 'rgba(33, 150, 243, 0.05)' : 'transparent',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    background: 'rgba(33, 150, 243, 0.08)',
                  }
                }}
              >
                {item.text}
              </Button>
            );
          })}
        </Box>
      )}

      {/* Mobile Navigation Drawer */}
      {currentUser && (
        <Drawer
          anchor="left"
          open={mobileMenuOpen}
          onClose={toggleMobileMenu}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': {
              width: 280,
              background: 'linear-gradient(135deg, #f0f4f8 0%, #e1e8ed 100%)',
              boxShadow: '0 8px 32px rgba(33, 150, 243, 0.2)',
            }
          }}
        >
          <Box sx={{ p: 3, borderBottom: '2px solid #2196F3' }}>
            <Typography 
              variant="h6" 
              sx={{ 
                fontWeight: 700,
                background: 'linear-gradient(45deg, #2196F3 0%, #1976D2 100%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              Navigation
            </Typography>
          </Box>
          <List sx={{ px: 2, pt: 2 }}>
            {navigationItems.map((item) => {
              const isActive = location.pathname === item.path;
              return (
                <ListItem
                  button
                  key={item.path}
                  onClick={() => handleNavigate(item.path)}
                  sx={{
                    mb: 1,
                    borderRadius: 2,
                    background: isActive 
                      ? 'linear-gradient(45deg, #2196F3 0%, #1976D2 100%)'
                      : '#fff',
                    color: isActive ? '#fff' : '#2196F3',
                    border: isActive ? 'none' : '1px solid rgba(33, 150, 243, 0.3)',
                    boxShadow: isActive ? '0 4px 12px rgba(33, 150, 243, 0.3)' : 'none',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      background: isActive 
                        ? 'linear-gradient(45deg, #1976D2 0%, #1565C0 100%)'
                        : 'rgba(33, 150, 243, 0.08)',
                      transform: 'translateX(4px)',
                    }
                  }}
                >
                  <ListItemIcon sx={{ color: 'inherit', minWidth: 40 }}>
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText 
                    primary={item.text}
                    primaryTypographyProps={{
                      fontWeight: isActive ? 600 : 500,
                      fontSize: '0.95rem'
                    }}
                  />
                </ListItem>
              );
            })}
          </List>
        </Drawer>
      )}
    </AppBar>
  );
};

export default Navbar;